import { Row, Col, Button, Skeleton, Typography, Tag, Table, Avatar, Modal } from "antd";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { ShowToast, Severty } from "../../helper/toast";
import {
    ArrowLeftOutlined,
    CalendarOutlined,
    EnvironmentOutlined,
    CopyOutlined,
    CheckCircleFilled,
    PlayCircleFilled,
    EditOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    DownOutlined,
    UpOutlined,
    EyeOutlined,
    UserOutlined,
    VideoCameraOutlined
} from "@ant-design/icons";
import moment from "moment";
import confirm from "antd/lib/modal/confirm";
import ViewTournamentLeaderBoard from "./ViewTournamentLeaderBoard";

const { Text } = Typography;

function ViewTournament() {
    const params = useParams();
    const navigate = useNavigate();
    const { request } = useRequest();
    const [tournament, setTournament] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expandedMatches, setExpandedMatches] = useState({});
    const [expandedDetails, setExpandedDetails] = useState({});
    const [activeVideo, setActiveVideo] = useState(null);
    const [isLeaderboardOpen, setIsLeaderboardOpen] = useState(false);

    const toggleMatchClips = (matchId) => {
        setExpandedMatches((prev) => ({
            ...prev,
            [matchId]: !prev[matchId],
        }));
    };

    const toggleMatchDetails = (matchId) => {
        setExpandedDetails((prev) => ({
            ...prev,
            [matchId]: !prev[matchId],
        }));
    };

    const getWinnerName = (match) => {
        if (!match.result) return null;
        if (match.result === "TEAM_A") {
            return match.teamA || match.team1Name || match.team1?.name || "Team A";
        }
        if (match.result === "TEAM_B") {
            return match.teamB || match.team2Name || match.team2?.name || "Team B";
        }
        return match.result; // if it's already a team name or custom message
    };

    const fetchData = (id) => {
        setLoading(true);
        request({
            url: `${apiPath.tournamentById}/${id}`,
            method: "GET",
            onSuccess: ({ data }) => {
                setLoading(false);
                setTournament(data?.data || data);
            },
            onError: (error) => {
                setLoading(false);
                ShowToast(error, Severty.ERROR);
            },
        });
    };

    useEffect(() => {
        if (params.id) {
            fetchData(params.id);
        }
    }, [params.id]);



    if (loading) {
        return (
            <div className="view-tournament-container" style={{ backgroundColor: "#ffffff", minHeight: "100vh", padding: "24px" }}>
                <Skeleton active paragraph={{ rows: 12 }} />
            </div>
        );
    }

    if (!tournament) {
        return (
            <div className="view-tournament-container" style={{ backgroundColor: "#ffffff", minHeight: "100vh", padding: "24px", textAlign: "center" }}>
                <Text style={{ color: "#6b7280" }}>No tournament data found.</Text>
            </div>
        );
    }

    // Dynamic calculations - count only members who are coaches (isCoach === true)
    const admins = tournament.adminsDetails || tournament.admins || (tournament.createdBy ? [tournament.createdBy] : []);
    const adminsCount = admins.length;
    const matchesCount = tournament.matches?.length || 0;
    const squadCount = (tournament.linkedGroups || []).reduce((acc, g) => {
        const members = g.membersDetails || g.members || [];
        const coaches = members.filter(m => m.isCoach || m.role?.name?.toLowerCase() === 'coach');
        return acc + coaches.length;
    }, 0);



    return (
        <div className="view-tournament-container">
            {/* Header / Actions Row */}
            <Row gutter={[24, 24]} align="middle" style={{ marginBottom: "20px" }}>
                <Col xs={12}>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate(-1)}
                        className="back-btn"
                    >
                        Back
                    </Button>
                </Col>
                <Col xs={12} style={{ textAlign: "right" }}>
                    <Button
                        type="primary"
                        onClick={() => setIsLeaderboardOpen(true)}
                        style={{ background: "#ea580c", borderColor: "#ea580c", fontWeight: "bold" }}
                    >
                        🏆 View Leaderboard
                    </Button>
                </Col>
            </Row>

            {/* Main Content Layout */}
            <Row gutter={[24, 24]}>
                <Col span={24}>

                    {/* 1. Gradient Details Banner Card */}
                    <div className="banner-card">
                        <Row gutter={[24, 24]} align="middle">
                            <Col xs={24} md={16}>
                                <div className="banner-tag-container">
                                    {tournament.format && (
                                        <span className="tag-format">{tournament.format.toUpperCase()}</span>
                                    )}
                                    {tournament.ballType && (
                                        <span className="tag-ball-type">{tournament.ballType.toUpperCase()} BALL</span>
                                    )}
                                    {tournament.privacy && (
                                        <span className="tag-privacy">{tournament.privacy.toUpperCase()}</span>
                                    )}
                                    <span className="tag-status-active">
                                        <span className="status-dot"></span>
                                        {(tournament.status || (tournament.isActive ? "ACTIVE" : "INACTIVE")).toUpperCase()}
                                    </span>
                                </div>
                                <span className="banner-label">Tournament Details</span>
                                <h1 className="banner-title">{tournament.name || tournament.title || "-"}</h1>
                                <p className="banner-desc">{tournament.description || "No description provided."}</p>
                            </Col>

                            <Col xs={24} md={8}>
                                <div className="banner-counters">
                                    <div className="counter-pill">
                                        <span className="counter-num">{matchesCount}</span>
                                        <span className="counter-label">MATCHES</span>
                                    </div>

                                    <div className="counter-pill">
                                        <span className="counter-num">{adminsCount}</span>
                                        <span className="counter-label">ADMINS</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    {/* 2. Overview Card */}
                    <div className="section-card">
                        <div className="section-title-row">
                            <h2 className="section-title">Overview</h2>
                        </div>

                        <Row gutter={[24, 24]}>
                            <Col xs={24} sm={12} md={8}>
                                <div className="field-label">Tournament Name</div>
                                <div className="field-value" style={{ fontWeight: 'bold' }}>
                                    {tournament.name || tournament.title || "-"}
                                </div>
                            </Col>
                            <Col xs={12} sm={6} md={4}>
                                <div className="field-label">Format</div>
                                <div>
                                    {tournament.format ? (
                                        <span className="tag-format">{tournament.format.toUpperCase()}</span>
                                    ) : "-"}
                                </div>
                            </Col>
                            <Col xs={12} sm={6} md={4}>
                                <div className="field-label">Ball Type</div>
                                <div className="field-value" style={{ fontWeight: 'bold' }}>
                                    {(tournament.ballType || "-").toUpperCase()}
                                </div>
                            </Col>
                            <Col xs={12} sm={12} md={4}>
                                <div className="field-label">Start Date</div>
                                <div className="field-value" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <CalendarOutlined style={{ color: '#f97316' }} />
                                    <span>
                                        {tournament.startDate ? moment(tournament.startDate).format("DD MMM YYYY") : "-"}
                                    </span>
                                </div>
                            </Col>
                            <Col xs={12} sm={12} md={4}>
                                <div className="field-label">End Date</div>
                                <div className="field-value" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <CalendarOutlined style={{ color: '#f97316' }} />
                                    <span>
                                        {tournament.endDate ? moment(tournament.endDate).format("DD MMM YYYY") : "-"}
                                    </span>
                                </div>
                            </Col>
                        </Row>

                        <Row gutter={[24, 24]} style={{ marginTop: "20px" }}>
                            <Col xs={12} sm={6} md={4}>
                                <div className="field-label">Status</div>
                                <span className="tag-status-active">
                                    <span className="status-dot"></span>
                                    {(tournament.status || (tournament.isActive ? "ACTIVE" : "INACTIVE")).toUpperCase()}
                                </span>
                            </Col>
                            <Col xs={12} sm={6} md={4}>
                                <div className="field-label">Privacy</div>
                                <Tag
                                    color="transparent"
                                    style={{
                                        backgroundColor: '#2a1715',
                                        color: '#f87171',
                                        border: 'none',
                                        borderRadius: '6px',
                                        padding: '4px 12px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {(tournament.privacy || "PUBLIC").toUpperCase()}
                                </Tag>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <div className="field-label">Location</div>
                                <div className="field-value" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <EnvironmentOutlined style={{ color: '#f97316' }} />
                                    <span>{tournament.location || "-"}</span>
                                </div>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <div className="field-label">Created By</div>
                                <div className="user-badge-container">
                                    <Avatar
                                        src={tournament.createdBy?.avatar}
                                        size={28}
                                        style={{ border: '1px solid #3c2a1e' }}
                                    >
                                        {tournament.createdBy?.name?.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <span className="user-badge-name">
                                        {tournament.createdBy?.name || tournament.createdBy?.username || "-"}
                                    </span>
                                </div>
                            </Col>
                        </Row>

                        <Row gutter={[24, 24]} style={{ marginTop: "20px" }}>
                            <Col xs={24} md={8}>
                                <div className="field-label">Tournament ID</div>
                                <div className="field-value-tag-id" >
                                    <span>{tournament._id || tournament.id || "-"}</span>

                                </div>
                            </Col>
                            <Col xs={24} md={16}>
                                <div className="field-label">Description</div>
                                <p className="field-value" style={{ margin: 0, color: '#d1d5db' }}>
                                    {tournament.description || "No description provided."}
                                </p>
                            </Col>
                        </Row>
                    </div>

                    {/* 3. Admins Card */}
                    <div className="section-card">
                        <div className="section-title-row">
                            <h2 className="section-title">Admins</h2>
                            <span className="section-subtitle">{adminsCount} {adminsCount === 1 ? 'person' : 'people'}</span>
                        </div>

                        <Row gutter={[16, 16]}>
                            {admins.length > 0 ? (
                                admins.map((admin, idx) => {
                                    const adminName = admin.name || admin.username || "Admin User";
                                    const adminHandle = admin.username || adminName.toLowerCase().replace(/\s+/g, '_');
                                    return (
                                        <Col xs={24} sm={12} key={admin._id || idx}>
                                            <div className="admin-grid-card">
                                                <div className="admin-info-left">
                                                    <Avatar
                                                        src={admin.avatar || admin.image}
                                                        size={40}
                                                        style={{ border: '2px solid #3c2a1e' }}
                                                    >
                                                        {adminName.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    <div>
                                                        <div className="admin-name-row">
                                                            <span className="admin-name">{adminName}</span>
                                                            <CheckCircleFilled style={{ color: '#f97316', fontSize: '12px' }} />
                                                        </div>
                                                        <div className="admin-handle">@{adminHandle}</div>
                                                    </div>
                                                </div>
                                                <span className="admin-tag-red">Admin</span>
                                            </div>
                                        </Col>
                                    );
                                })
                            ) : (
                                <Col span={24}>
                                    <div style={{ textAlign: "center", color: "#9ca3af", padding: "12px 0" }}>No admins designated.</div>
                                </Col>
                            )}
                        </Row>
                    </div>

                    {/* 4. Linked Groups Card */}
                    <div className="section-card">
                        <div className="section-title-row">
                            <h2 className="section-title">Linked Groups</h2>
                            <span className="section-subtitle">{squadCount} {squadCount === 1 ? 'member' : 'members'}</span>
                        </div>

                        {tournament.linkedGroups && tournament.linkedGroups.length > 0 ? (
                            tournament.linkedGroups.map((group, idx) => {
                                // Filter group members to only show coaches
                                const groupMembers = (group.membersDetails || group.members || []).filter(
                                    member => member.isCoach || member.role?.name?.toLowerCase() === 'coach'
                                );

                                return (
                                    <div key={group._id || idx} style={{ marginBottom: idx < tournament.linkedGroups.length - 1 ? "32px" : "0" }}>
                                        <div className="group-info-card">
                                            <Avatar
                                                src={group.image || group.logo}
                                                shape="square"
                                                size={56}
                                                style={{ borderRadius: '8px', border: '1px solid #1f232b' }}
                                            >
                                                {group.name?.charAt(0).toUpperCase() || 'G'}
                                            </Avatar>
                                            <div className="group-details">
                                                <span className="group-name">{group.name}</span>
                                                <span className="group-desc">{group.description || group.category || "Batting"}</span>
                                            </div>
                                        </div>

                                        <div className="linked-members-heading">Linked Members</div>

                                        <Row gutter={[16, 16]}>
                                            {groupMembers.length > 0 ? (
                                                groupMembers.map((member, mIdx) => {
                                                    const memberName = member.name || member.username || "Member";
                                                    const handle = member.username || memberName.toLowerCase().replace(/\s+/g, '_');
                                                    const isCoach = member.isCoach || member.role?.name?.toLowerCase() === 'coach';
                                                    const isAcademy = member.isAcademy || member.role?.name?.toLowerCase() === 'academy';
                                                    const ageGroup = member.ageCategory || "ADULT";

                                                    return (
                                                        <Col xs={24} sm={12} md={8} key={member._id || mIdx}>
                                                            <div className="member-grid-card">
                                                                <div>
                                                                    <div className="member-top-row">
                                                                        <Avatar
                                                                            src={member.avatar || member.image}
                                                                            size={44}
                                                                            style={{ backgroundColor: '#e2700d', color: '#ffffff', fontWeight: 'bold' }}
                                                                        >
                                                                            {memberName.charAt(0).toUpperCase()}
                                                                        </Avatar>
                                                                        <div className="member-name-col">
                                                                            <span className="member-name">
                                                                                {memberName}
                                                                                <CheckCircleFilled style={{ color: '#f97316', fontSize: '12px' }} />
                                                                            </span>
                                                                            <span className="member-handle">@{handle}</span>
                                                                        </div>
                                                                    </div>

                                                                    <div className="member-location-row">
                                                                        <EnvironmentOutlined style={{ color: '#f97316' }} />
                                                                        <span>
                                                                            {member.location ?
                                                                                [member.location.city, member.location.country].filter(Boolean).join(", ")
                                                                                : "Jaipur, Rajasthan"
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                <div>
                                                                    <div className="member-tags-row">
                                                                        {isCoach && <span className="member-tag-default">COACH</span>}
                                                                        {isAcademy && <span className="member-tag-default">ACADEMY</span>}
                                                                        {ageGroup && <span className="member-tag-pink">{ageGroup.toUpperCase()}</span>}
                                                                    </div>

                                                                    <div className="member-points-row">
                                                                        <span className="member-points-label">POINTS</span>
                                                                        <span className="member-points-val">{member.points ?? 0}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    );
                                                })
                                            ) : (
                                                <Col span={24}>
                                                    <div style={{ padding: '16px', color: '#6b7280', textAlign: 'center', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                                        No coaches linked to this group.
                                                    </div>
                                                </Col>
                                            )}
                                        </Row>
                                    </div>
                                );
                            })
                        ) : (
                            <div style={{ textAlign: "center", color: "#6b7280", padding: "12px 0" }}>No linked groups found.</div>
                        )}
                    </div>

                    {/* 5. Matches Card */}
                    <div className="section-card">
                        <div className="section-title-row">
                            <h2 className="section-title">Matches</h2>
                            <span className="section-subtitle">{matchesCount} {matchesCount === 1 ? 'scheduled' : 'scheduled'}</span>
                        </div>

                        {tournament.matches && tournament.matches.length > 0 ? (
                            <div className="matches-list-container">
                                {tournament.matches.map((match) => {
                                    const matchId = match._id || match.id;
                                    const isExpanded = !!expandedMatches[matchId];
                                    const isDetailsExpanded = !!expandedDetails[matchId];
                                    const teamAName = match.teamA || match.team1Name || match.team1?.name || "Team A";
                                    const teamBName = match.teamB || match.team2Name || match.team2?.name || "Team B";
                                    const teamALogo = match.teamImageA || match.team1Logo || match.team1?.logo || match.team1?.image;
                                    const teamBLogo = match.teamImageB || match.team2Logo || match.team2?.logo || match.team2?.image;

                                    const winnerName = getWinnerName(match);

                                    // Score logic
                                    let scoreDisplay = "-";
                                    if (match.finalScore && (match.finalScore.teamA !== undefined || match.finalScore.teamB !== undefined)) {
                                        scoreDisplay = `${match.finalScore.teamA ?? 0} - ${match.finalScore.teamB ?? 0}`;
                                    } else if (match.team1Score !== undefined && match.team2Score !== undefined) {
                                        scoreDisplay = `${match.team1Score} - ${match.team2Score}`;
                                    } else if (match.score) {
                                        scoreDisplay = match.score;
                                    }

                                    const postsCount = match.posts?.length || 0;

                                    return (
                                        <div key={matchId} className="match-card-modern">
                                            {/* Match Card Header */}
                                            <div className="match-card-header">
                                                <div className="match-card-title-block">
                                                    <span className="match-card-name">{match.name || match.title || "My League"}</span>
                                                    <div className="match-card-loc-date">
                                                        {match.location && (
                                                            <div className="match-card-meta-item">
                                                                <EnvironmentOutlined style={{ color: '#f97316' }} />
                                                                <span>{match.location}</span>
                                                            </div>
                                                        )}
                                                        <div className="match-card-meta-item">
                                                            <CalendarOutlined style={{ color: '#f97316' }} />
                                                            <span>
                                                                {match.matchDate ? moment(match.matchDate).format("DD MMM YYYY • HH:mm [UTC]") : "TBD"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                {match.mediaUrl && (
                                                    <Button
                                                        className="match-watch-btn"
                                                        icon={<PlayCircleFilled style={{ color: '#f97316' }} />}
                                                        onClick={() => setActiveVideo({
                                                            url: match.mediaUrl,
                                                            title: match.name || "Match Video",
                                                            caption: match.description || match.summary || "Full Match Recording",
                                                            thumbnailUrl: match.thumbnailUrl,
                                                            author: "Tournament Admin",
                                                            views: 1,
                                                            skillType: match.ballType || "CRICKET",
                                                            clipType: "FULL_MATCH"
                                                        })}
                                                    >
                                                        Watch Match
                                                    </Button>
                                                )}
                                            </div>

                                            {/* Match Card Body */}
                                            <div className="match-card-body">
                                                {/* Left side: Teams and score */}
                                                <div className="match-card-board">
                                                    <div className="match-teams-vs-row">
                                                        <div className="match-team-block">
                                                            <Avatar 
                                                                src={teamALogo} 
                                                                size={56} 
                                                                className="team-logo-large"
                                                            >
                                                                {teamAName.charAt(0).toUpperCase()}
                                                            </Avatar>
                                                            <span className="team-name-bold">{teamAName}</span>
                                                        </div>

                                                        <div className="match-vs-score-block">
                                                            <span className="vs-text-styled">vs</span>
                                                            <span className="score-text-styled">{scoreDisplay}</span>
                                                        </div>

                                                        <div className="match-team-block">
                                                            <Avatar 
                                                                src={teamBLogo} 
                                                                size={56} 
                                                                className="team-logo-large"
                                                            >
                                                                {teamBName.charAt(0).toUpperCase()}
                                                            </Avatar>
                                                            <span className="team-name-bold">{teamBName}</span>
                                                        </div>
                                                    </div>

                                                    {winnerName && (
                                                        <div className="match-result-bar">
                                                            <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 600 }}>MATCH RESULT</span>
                                                            <span className="winner-pill">Winner: {winnerName}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Right side: Match cover image */}
                                                <div 
                                                    className="match-cover-preview"
                                                    onClick={() => {
                                                        if (match.mediaUrl) {
                                                            setActiveVideo({
                                                                url: match.mediaUrl,
                                                                title: match.name || "Match Video",
                                                                caption: match.description || match.summary || "Full Match Recording",
                                                                thumbnailUrl: match.thumbnailUrl,
                                                                author: "Tournament Admin",
                                                                views: 1,
                                                                skillType: match.ballType || "CRICKET",
                                                                clipType: "FULL_MATCH"
                                                            });
                                                        } else if (match.thumbnailUrl) {
                                                            Modal.info({
                                                                title: match.name || 'Match Cover',
                                                                content: (
                                                                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                                                                        <img src={match.thumbnailUrl} alt="Cover" style={{ maxWidth: '100%', borderRadius: '8px' }} />
                                                                    </div>
                                                                ),
                                                                okText: 'Close',
                                                                maskClosable: true
                                                            });
                                                        }
                                                    }}
                                                >
                                                    {match.thumbnailUrl ? (
                                                        <>
                                                            <img 
                                                                src={match.thumbnailUrl} 
                                                                alt="Match Cover" 
                                                                className="match-cover-img"
                                                            />
                                                            <div className="match-cover-overlay">
                                                                <PlayCircleFilled style={{ color: '#ffffff', fontSize: '36px' }} />
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="match-cover-fallback">
                                                            <VideoCameraOutlined style={{ fontSize: '24px' }} />
                                                            <span className="match-cover-fallback-title">CrickClips Match</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Match Card Footer - Toggles & Details */}
                                            <div className="match-card-footer">
                                                <div className="details-action-bar" style={{ marginBottom: isDetailsExpanded || isExpanded ? '12px' : '0' }}>
                                                    <button 
                                                        className="clips-toggle-btn"
                                                        onClick={() => toggleMatchDetails(matchId)}
                                                    >
                                                        {isDetailsExpanded ? <UpOutlined /> : <DownOutlined />}
                                                        <span>Match Info & Stats</span>
                                                    </button>
                                                    
                                                    <button 
                                                        className="clips-toggle-btn"
                                                        onClick={() => toggleMatchClips(matchId)}
                                                    >
                                                        {isExpanded ? <UpOutlined /> : <DownOutlined />}
                                                        <span>Highlight Clips ({postsCount})</span>
                                                    </button>
                                                </div>

                                                {/* Expanded Match Details & Stats */}
                                                {isDetailsExpanded && (
                                                    <div className="match-details-container">
                                                        <div className="details-grid">
                                                            {/* Left panel: Summary & Stats */}
                                                            <div>
                                                                {/* Summary */}
                                                                {match.summary && (
                                                                    <div className="match-summary-block">
                                                                        <div className="match-summary-title">Match Summary</div>
                                                                        <div className="match-summary-content">
                                                                            {match.summary}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                
                                                                {/* Performance Stats */}
                                                                <div>
                                                                    <div className="match-summary-title" style={{ marginBottom: '8px' }}>Performance Stats</div>
                                                                    <div className="match-stats-grid">
                                                                        <div className="match-stat-card">
                                                                            <span className="match-stat-val">{match.performanceStats?.boundaries ?? 0}</span>
                                                                            <span className="match-stat-lbl">Boundaries</span>
                                                                        </div>
                                                                        <div className="match-stat-card">
                                                                            <span className="match-stat-val">{match.performanceStats?.sixes ?? 0}</span>
                                                                            <span className="match-stat-lbl">Sixes</span>
                                                                        </div>
                                                                        <div className="match-stat-card">
                                                                            <span className="match-stat-val">{match.performanceStats?.wickets ?? 0}</span>
                                                                            <span className="match-stat-lbl">Wickets</span>
                                                                        </div>
                                                                        <div className="match-stat-card">
                                                                            <span className="match-stat-val">{match.performanceStats?.catches ?? 0}</span>
                                                                            <span className="match-stat-lbl">Catches</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Right panel: Metadata */}
                                                            <div className="match-meta-info-card">
                                                                <div className="match-meta-title">System & Game Info</div>
                                                                
                                                                <div className="match-meta-row">
                                                                    <span className="match-meta-lbl">Ball Type</span>
                                                                    <span className="match-meta-val">
                                                                        <Tag color="orange" style={{ margin: 0, fontWeight: 700 }}>
                                                                            {(match.ballType || "CRICKET").toUpperCase()}
                                                                        </Tag>
                                                                    </span>
                                                                </div>

                                                                <div className="match-meta-row">
                                                                    <span className="match-meta-lbl">Status</span>
                                                                    <span>
                                                                        {match.isActive ? (
                                                                            <span className="tag-status-active-pill">Active</span>
                                                                        ) : (
                                                                            <span className="tag-status-inactive-pill">Inactive</span>
                                                                        )}
                                                                    </span>
                                                                </div>

                                                                <div className="match-meta-row">
                                                                    <span className="match-meta-lbl">Deleted</span>
                                                                    <span>
                                                                        {match.isDeleted ? (
                                                                            <span className="tag-status-deleted-pill">Yes</span>
                                                                        ) : (
                                                                            <span className="tag-status-notdeleted-pill">No</span>
                                                                        )}
                                                                    </span>
                                                                </div>

                                                                <div className="match-meta-row">
                                                                    <span className="match-meta-lbl">Match Date</span>
                                                                    <span className="match-meta-val">
                                                                        {match.matchDate ? moment(match.matchDate).format("DD MMM YYYY • HH:mm") : "TBD"}
                                                                    </span>
                                                                </div>

                                                                <div className="match-meta-row">
                                                                    <span className="match-meta-lbl">Created At</span>
                                                                    <span className="match-meta-val">
                                                                        {match.createdAt ? moment(match.createdAt).format("DD MMM YYYY • HH:mm") : "-"}
                                                                    </span>
                                                                </div>

                                                                <div className="match-meta-row">
                                                                    <span className="match-meta-lbl">Updated At</span>
                                                                    <span className="match-meta-val">
                                                                        {match.updatedAt ? moment(match.updatedAt).format("DD MMM YYYY • HH:mm") : "-"}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Expanded highlight videos */}
                                                {isExpanded && (
                                                    <div className="clips-grid-container">
                                                        <h4 className="clips-section-title">Clips from this Match</h4>
                                                        {postsCount > 0 ? (
                                                            <div className="clips-grid">
                                                                {match.posts.map((post) => {
                                                                    const postId = post._id || post.postId;
                                                                    const postCaption = post.caption || "CrickClips Highlight";
                                                                    const postAuthor = post.author || "Anonymous";
                                                                    const postViews = post.views ?? 0;
                                                                    const postThumb = post.thumbnailUrl || match.thumbnailUrl;
                                                                    const postVideoUrl = post.mediaUrl;

                                                                    return (
                                                                        <div 
                                                                            key={postId} 
                                                                            className="clip-card"
                                                                            onClick={() => {
                                                                                if (postVideoUrl) {
                                                                                    setActiveVideo({
                                                                                        url: postVideoUrl,
                                                                                        title: `Clip: ${postCaption}`,
                                                                                        caption: postCaption,
                                                                                        thumbnailUrl: postThumb,
                                                                                        author: postAuthor,
                                                                                        views: postViews,
                                                                                        skillType: post.skillType,
                                                                                        clipType: post.clipType
                                                                                    });
                                                                                } else {
                                                                                    ShowToast("Video URL not available for this clip", Severty.ERROR);
                                                                                }
                                                                            }}
                                                                        >
                                                                            <div className="clip-thumb-wrapper">
                                                                                {postThumb ? (
                                                                                    <img 
                                                                                        src={postThumb} 
                                                                                        alt="Clip Thumbnail" 
                                                                                        className="clip-thumb-img"
                                                                                    />
                                                                                ) : (
                                                                                    <div className="match-cover-fallback" style={{ height: '100%' }}>
                                                                                        <VideoCameraOutlined style={{ fontSize: '20px' }} />
                                                                                    </div>
                                                                                )}
                                                                                <div className="clip-play-overlay">
                                                                                    <PlayCircleFilled className="clip-play-icon" />
                                                                                </div>
                                                                                {post.clipType && (
                                                                                    <span className="clip-meta-badge">{post.clipType}</span>
                                                                                )}
                                                                            </div>

                                                                            <div className="clip-info-body">
                                                                                <h5 className="clip-caption">{postCaption}</h5>
                                                                                
                                                                                <div className="clip-author-row">
                                                                                    <UserOutlined style={{ color: '#f97316' }} />
                                                                                    <span>@{postAuthor}</span>
                                                                                </div>

                                                                                <div className="clip-footer-stats">
                                                                                    <div className="clip-stat-item">
                                                                                        <EyeOutlined />
                                                                                        <span>{postViews} views</span>
                                                                                    </div>

                                                                                    <div className="clip-tags-row">
                                                                                        {post.skillType && (
                                                                                            <span className="clip-tag-pill tag-pill-skill">
                                                                                                {post.skillType}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : (
                                                            <div style={{ color: '#9ca3af', fontSize: '13px', fontStyle: 'italic', padding: '8px 0' }}>
                                                                No clips uploaded for this match yet.
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div style={{ textAlign: "center", color: "#6b7280", padding: "24px 0" }}>
                                No matches scheduled.
                            </div>
                        )}
                    </div>

                    {/* Tournament Leaderboard Modal */}
                    <ViewTournamentLeaderBoard
                        open={isLeaderboardOpen}
                        onCancel={() => setIsLeaderboardOpen(false)}
                        tournamentId={tournament._id || tournament.id}
                    />

                    {/* Footer */}
                    <div className="footer-text">
                        Copyright © 2026. All Rights Reserved - Version 1.0
                    </div>

                    {/* Premium Video Player Modal */}
                    <Modal
                        title={activeVideo?.title || "Video Player"}
                        open={!!activeVideo}
                        onCancel={() => setActiveVideo(null)}
                        footer={null}
                        width={800}
                        destroyOnClose
                        className="premium-video-modal"
                        centered
                    >
                        {activeVideo && (
                            <div>
                                <div className="player-wrapper">
                                    <video
                                        src={activeVideo.url}
                                        controls
                                        autoPlay
                                        className="player-video"
                                        controlsList="nodownload"
                                    />
                                </div>
                                <div className="video-info-section">
                                    <div className="video-caption-text">
                                        {activeVideo.caption}
                                    </div>
                                    <div className="video-meta-row">
                                        <span>Uploaded by: <strong>@{activeVideo.author}</strong></span>
                                        <span>Views: <strong>{activeVideo.views}</strong></span>
                                        {activeVideo.clipType && <span>Type: <strong>{activeVideo.clipType}</strong></span>}
                                    </div>
                                    <div className="video-meta-badge-grid">
                                        {activeVideo.skillType && (
                                            <span className="clip-tag-pill tag-pill-skill" style={{ fontSize: '11px', padding: '4px 10px' }}>
                                                Skill Focus: {activeVideo.skillType}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </Modal>

                </Col>
            </Row>
        </div>
    );
}

export default ViewTournament;
